import { useRef, useState, useEffect } from "react";
import { Undo2, X, Check } from "lucide-react";

/**
 * Minimal finger-drawing canvas. Outputs PNG with transparent bg.
 * Strokes stored in memory so we can undo. One color (foreground), variable thickness.
 */
export default function DrawingCanvas({ onCancel, onSave }) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const strokesRef = useRef([]); // array of arrays of {x,y}
  const currentStrokeRef = useRef(null);
  const [hasInk, setHasInk] = useState(false);

  // Match canvas pixel size to displayed size for crisp drawing
  useEffect(() => {
    const c = canvasRef.current;
    const resize = () => {
      const rect = c.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      c.width = rect.width * dpr;
      c.height = rect.height * dpr;
      const ctx = c.getContext("2d");
      ctx.scale(dpr, dpr);
      redraw();
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
    // eslint-disable-next-line
  }, []);

  function getInkColor() {
    return getComputedStyle(document.documentElement)
      .getPropertyValue("--foreground")
      .trim();
  }

  function redraw() {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = `hsl(${getInkColor()})`;
    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
      ctx.stroke();
    }
  }

  function pos(e) {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return { x: point.clientX - rect.left, y: point.clientY - rect.top };
  }

  function start(e) {
    e.preventDefault();
    drawingRef.current = true;
    currentStrokeRef.current = [pos(e)];
    strokesRef.current.push(currentStrokeRef.current);
  }
  function move(e) {
    if (!drawingRef.current) return;
    e.preventDefault();
    currentStrokeRef.current.push(pos(e));
    redraw();
    if (!hasInk) setHasInk(true);
  }
  function end() {
    drawingRef.current = false;
    currentStrokeRef.current = null;
  }
  function undo() {
    strokesRef.current.pop();
    setHasInk(strokesRef.current.length > 0);
    redraw();
  }
  function save() {
    // Render to a fresh canvas without DPR scaling for cleaner export
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const out = document.createElement("canvas");
    out.width = rect.width * 2;
    out.height = rect.height * 2;
    const ctx = out.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.2;
    ctx.strokeStyle = `hsl(${getInkColor()})`;
    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) ctx.lineTo(stroke[i].x, stroke[i].y);
      ctx.stroke();
    }
    onSave(out.toDataURL("image/png"));
  }

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="drawing-overlay">
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="meta-label text-muted-foreground">draw a thought</span>
        <div className="flex items-center gap-1">
          <button
            onClick={undo}
            disabled={!hasInk}
            className="p-2 rounded hover:bg-secondary disabled:opacity-30"
            aria-label="undo"
            data-testid="drawing-undo-button"
          >
            <Undo2 className="w-4 h-4" />
          </button>
          <button
            onClick={onCancel}
            className="p-2 rounded hover:bg-secondary"
            aria-label="cancel"
            data-testid="drawing-cancel-button"
          >
            <X className="w-4 h-4" />
          </button>
          <button
            onClick={save}
            disabled={!hasInk}
            className="p-2 rounded hover:bg-secondary disabled:opacity-30"
            aria-label="save"
            data-testid="drawing-save-button"
          >
            <Check className="w-4 h-4" />
          </button>
        </div>
      </div>
      <canvas
        ref={canvasRef}
        className="block w-full touch-none"
        style={{ height: "260px", background: "transparent" }}
        onMouseDown={start}
        onMouseMove={move}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={move}
        onTouchEnd={end}
        data-testid="drawing-canvas"
      />
    </div>
  );
}
